import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertCircle, Building2, Loader2, RefreshCw } from 'lucide-react';
import { Device } from '../../types/device';
import { fetchDeviceProjectList } from '../../../../redux/device/actions';
import { Button } from '../../../../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';

interface DeviceProjectHistoryProps {
    device: Device;
}

interface DeviceProjectListItem {
    id?: number | string | null;
    deviceRegistrationId?: number | string | null;
    deviceId?: string | null;
    project_id?: number | string | null;
    projectId?: number | string | null;
    project_name?: string | null;
    projectName?: string | null;
    name?: string | null;
    project_number?: string | null;
    projectNumber?: string | null;
    user_id?: number | string | null;
    userId?: number | string | null;
    weld_uid?: string | null;
    weld_type?: string | null;
    UpdatedBy?: string | null;
    createdAt?: string | null;
}

const formatHistoryDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const ErrorState = ({ message }: { message: string }) => (
    <div className="flex items-center gap-2 p-3 bg-[rgba(235,28,55,0.06)] border border-[rgba(235,28,55,0.2)] rounded-lg">
        <AlertCircle className="h-4 w-4 text-hero-red flex-shrink-0" />
        <p className="text-[13px] text-hero-red">{message}</p>
    </div>
);

export const DeviceProjectHistory: React.FC<DeviceProjectHistoryProps> = ({ device }) => {
    const dispatch = useDispatch();
    const {
        deviceProjectList = [],
        deviceProjectListLoading = false,
        deviceProjectListError = null,
    } = useSelector((state: any) => state.deviceData || {});

    const selectedDeviceId = String(device.id || '');

    const handleFetchDeviceProjectList = useCallback(() => {
        if (!selectedDeviceId) {
            return;
        }

        dispatch(fetchDeviceProjectList({
            deviceId: selectedDeviceId,
        }) as any);
    }, [dispatch, selectedDeviceId]);

    useEffect(() => {
        handleFetchDeviceProjectList();
    }, [handleFetchDeviceProjectList]);

    return (
        <div className="mt-6 bg-white rounded-lg border border-[#E2E5EA]">
            <div className="px-6 py-4 border-b border-[#E2E5EA] flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h3 className="text-[14px] font-semibold text-blueprint-blue flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-heritage-grey" />
                        Used Project List
                    </h3>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleFetchDeviceProjectList}
                    disabled={deviceProjectListLoading || !selectedDeviceId}
                    className="border-[#E2E5EA] text-text-grey hover:bg-page-bg text-[12px]"
                >
                    {deviceProjectListLoading ? (
                        <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            Loading...
                        </>
                    ) : (
                        <>
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Refresh
                        </>
                    )}
                </Button>
            </div>

            <div className="p-6">
                {!selectedDeviceId ? (
                    <div className="flex items-center gap-2 p-3 bg-[rgba(230,161,0,0.08)] border border-[rgba(230,161,0,0.2)] rounded-lg">
                        <AlertCircle className="h-4 w-4 text-[#B38600] flex-shrink-0" />
                        <p className="text-[13px] text-[#B38600]">
                            Select a device to load used projects.
                        </p>
                    </div>
                ) : deviceProjectListError ? (
                    <ErrorState message={deviceProjectListError} />
                ) : deviceProjectListLoading ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-[13px] text-heritage-grey">
                        <Loader2 className="h-4 w-4 animate-spin text-hero-red" />
                        Loading used project list...
                    </div>
                ) : deviceProjectList.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Project Number</TableHead>
                                <TableHead>Project Name</TableHead>
                                {/* <TableHead>Weld UID</TableHead> */}
                                <TableHead>Weld Machine</TableHead>
                                <TableHead>Assigned By</TableHead>
                                <TableHead>Assigned At</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {deviceProjectList.map((project: DeviceProjectListItem, index: number) => {
                                const projectId = project.project_id ?? project.projectId ?? project.id ?? '-';
                                const projectNumber = project.project_number ?? project.projectNumber ?? '-';
                                const projectName = project.project_name ?? project.projectName ?? project.name ?? '-';
                                const weldUid = project.weld_uid ?? '-';
                                const weldType = project.weld_type ?? '-';
                                const updatedBy = project.UpdatedBy ?? project.user_id ?? project.userId ?? '-';
                                const createdAt = formatHistoryDate(project.createdAt);

                                return (
                                    <TableRow key={`${projectId}-${index}`}>
                                        <TableCell>{projectNumber}</TableCell>
                                        <TableCell>{projectName}</TableCell>
                                        {/* <TableCell className="font-mono">{weldUid}</TableCell> */}
                                        <TableCell className="uppercase">{weldType}</TableCell>
                                        <TableCell>{updatedBy}</TableCell>
                                        <TableCell>{createdAt}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                ) : (
                    <div className="text-center py-8">
                        <Building2 className="mx-auto h-8 w-8 text-heritage-grey" />
                        <p className="mt-2 text-[13px] font-medium text-blueprint-blue">No used projects found</p>
                        <p className="mt-1 text-[12px] text-heritage-grey">
                            This selected device has no project records.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DeviceProjectHistory;
